import { Link, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import {
  instrumentPath,
  instrumentsPath,
  planningAdminInstrumentTypePath,
} from '@/shared/lib/routes'
import type WebInstrumentsInstrumentRef from '@/shared/types/web/WebInstrumentsInstrumentRef'
import type WebInstrumentsInstrumentTaskRef from '@/shared/types/web/WebInstrumentsInstrumentTaskRef'

type DataFieldRow = {
  id: number
  label: string
  required: boolean
  requiresValidation: boolean
  dataFieldType: string | null
  editHref: string
}

type PageProps = {
  instrument: WebInstrumentsInstrumentRef
  instrumentTask: WebInstrumentsInstrumentTaskRef
  dataFields: DataFieldRow[]
  newHref: string
  errors?: Record<string, string | string[]>
}

const DataFieldsIndexPage = () => {
  const {
    instrument,
    instrumentTask,
    dataFields,
    errors: sharedErrors,
  } = usePage<PageProps>().props

  useBreadcrumbs([
    { label: t('nav.instruments'), href: instrumentsPath() },
    { label: instrument.instrumentLabel, href: instrumentPath(instrument.id) },
    { label: t('instruments.show.tabs.tasks'), href: `${instrumentPath(instrument.id)}#tasks` },
    { label: t('instruments.taskDataFields.indexTitle') },
  ])

  // Data field definitions are owned by the instrument type (type-level), so editing
  // them here would change every instrument of the type. The task view is read-only;
  // editing is delegated to Admin → Instrument Types (legacy parity).
  const editInAdminHref = instrument.instrumentTypeId
    ? planningAdminInstrumentTypePath(instrument.instrumentTypeId)
    : null

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t('instruments.taskDataFields.indexTitle')}</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {instrument.instrumentLabel} · {instrumentTask.name}
          </p>
          <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
            {t('instruments.taskDataFields.indexSubtitle')}
          </p>
        </div>
        {editInAdminHref ? (
          <Button asChild variant="outline">
            <Link href={editInAdminHref}>{t('instruments.taskDataFields.editInAdmin')}</Link>
          </Button>
        ) : null}
      </div>

      <div
        className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700 dark:text-amber-400"
        role="note"
      >
        {t('instruments.taskDataFields.typeLevelNotice')}
      </div>

      {sharedErrors?.base && (
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {(Array.isArray(sharedErrors.base) ? sharedErrors.base : [sharedErrors.base]).join(' ')}
        </div>
      )}

      {dataFields.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 border rounded-md px-4">
          {t('instruments.taskDataFields.empty')}
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('instruments.taskDataFields.tableLabel')}</TableHead>
                <TableHead>{t('instruments.taskDataFields.tableType')}</TableHead>
                <TableHead>{t('instruments.taskDataFields.tableRequired')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dataFields.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.label}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {row.dataFieldType ?? t('instruments.show.noData')}
                  </TableCell>
                  <TableCell className="text-sm">
                    {row.required
                      ? t('instruments.taskDataFields.yes')
                      : t('instruments.taskDataFields.no')}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}

export default DataFieldsIndexPage
